import { UserManager, User, WebStorageStateStore, Log } from 'oidc-client-ts';
import {
  logLoginSuccess,
  logLogout,
  logTokenRefreshFailed,
  logTokenExpired
} from '@/utils/securityMonitor';
import { cleanAllAuthStorage } from './storageUtils';

// ✅ 优化 #3a: 条件化OIDC日志 - 仅在非生产环境启用详细日志
if (import.meta.env.MODE !== 'master' && import.meta.env.MODE !== 'production') {
  // Log.setLogger(console);
  Log.setLevel(Log.INFO);
} else {
  // 生产环境仅记录错误
  Log.setLevel(Log.ERROR);
}

// 当前环境信息
const BASE_URL = import.meta.env.VITE_APP_BASE_URL?.replace(/\/$/, '') || window.location.origin;
// ⚠️ 重要：使用API网关作为OIDC authority，网关会反向代理到SSO服务器
// 这样可以避免浏览器的CORS跨域限制

// ✅ 优化 #3b: 条件化初始化日志 - 仅开发环境打印
if (import.meta.env.MODE === 'development') {



}
// OIDC配置
const createOidcConfig = () => {
  const BASE_URL = import.meta.env.VITE_APP_BASE_URL?.replace(/\/$/, '') || window.location.origin;
  // ⚠️ 重要：使用API网关作为OIDC authority，网关会反向代理到SSO服务器
  const AUTHORITY_URL = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '');

  return {
    authority: AUTHORITY_URL,
    client_id: import.meta.env.VITE_OIDC_CLIENT_ID,
    client_secret: import.meta.env.VITE_OIDC_CLIENT_SECRET,
    redirect_uri: `${BASE_URL}${import.meta.env.VITE_OIDC_REDIRECT_URI}`,
    post_logout_redirect_uri: `${BASE_URL}/login`,
    response_type: 'code',
    scope: import.meta.env.VITE_OIDC_SCOPE || 'api openid profile offline_access',

    // // 使用localStorage存储用户信息和tokens
    userStore: new WebStorageStateStore({
      store: window.localStorage,
      prefix: 'oidc.'
    }),


    // 安全设置
    filterProtocolClaims: true,
    loadUserInfo: true,

    // 自动静默更新token
    automaticSilentRenew: true,
    silent_redirect_uri: `${BASE_URL}/silent-renew.html`,
    silentRequestTimeoutInSeconds: 10,
    // ✅ 优化: 提前5分钟刷新token，减少过期风险
    accessTokenExpiringNotificationTimeInSeconds: 300, // 默认60秒，增加到300秒（5分钟）

    // ✅ 关键修复：关闭 monitorSession
    // 原因：monitorSession 会检测 SSO 会话状态，即便 access_token 仍在 24h 内，
    // 只要 OP 会话失效/第三方 Cookie 受限/检查异常，就会触发 addUserSignedOut 强制登出
    // 这与 "token 24h" 的业务预期不符
    monitorSession: false, // ✅ 关闭会话监控。如果需要，可改为通过 access_token 过期来触发重新登录
    // checkSessionIntervalInSeconds: 30, // 不再需要
    includeIdTokenInSilentRenew: true
  };
};

// ✅ 关键修复：移除 testMgr，避免双重 UserManager 实例导致的竞态和状态污染
// 原先的 testMgr 和 getUserManager() 同时存在，会带来重复的 silent renew/session monitor
// 现在统一使用 getUserManager() 单例
// 为了向后兼容，testMgr 作为 getUserManager() 的延迟别名
// 注意：这不会创建新实例，而是返回同一个单例
export const testMgr = {
  get signinRedirect() { return getUserManager().signinRedirect.bind(getUserManager()); },
  get signinRedirectCallback() { return getUserManager().signinRedirectCallback.bind(getUserManager()); },
  get signinSilent() { return getUserManager().signinSilent.bind(getUserManager()); },
  get signoutRedirect() { return getUserManager().signoutRedirect.bind(getUserManager()); },
  get getUser() { return getUserManager().getUser.bind(getUserManager()); },
  get events() { return getUserManager().events; },
};

// 创建并导出OIDC UserManager实例
let _userManager: UserManager | null = null;

export const getUserManager = (): UserManager => {
  if (!_userManager) {
    _userManager = new UserManager(createOidcConfig());

    // 捕获manager实例用于事件处理器
    const manager = _userManager;

    // 添加事件监听器用于调试和错误处理
    manager.events.addUserLoaded((user) => {


      // 🛡️ 安全监控：记录登录成功
      logLoginSuccess(user?.profile?.sub);
    });

    manager.events.addAccessTokenExpiring(() => {

    });

    manager.events.addAccessTokenExpired(() => {

      // 🛡️ 安全监控：记录Token过期
      logTokenExpired();
      // 自动静默刷新会处理此情况
    });

    // 静默刷新错误处理
    manager.events.addSilentRenewError((error) => {
      // 🛡️ 安全监控：记录Token刷新失败
      logTokenRefreshFailed(error);



      // 🔍 调试：检查localStorage状态


      // 如果静默刷新失败，清除本地存储并重定向到登录
      if (error.message.includes('login_required') ||
        error.message.includes('interaction_required') ||
        error.message.includes('consent_required')) {


        // 🔍 调试：记录清除前的状态


        // ✅ 优化：使用精确清理，避免误删其他应用数据
        cleanAllAuthStorage();
        sessionStorage.clear();


        manager.signinRedirect({ state: window.location.href });
      }
    });

    // 用户登出事件处理
    // ✅ 关键修复：不再在 addUserSignedOut 中直接清理存储和跳转
    // 原因：这个事件可能由于第三方 Cookie 受限、网络抖动等误触发
    // 新策略：仅记录日志，让 401 响应处理器来决定是否真的需要登出
    manager.events.addUserSignedOut(() => {
      // 🛡️ 安全监控：记录登出事件
      logLogout();

      // ✅ 不再直接清理和跳转，避免误杀
      // 如果 access_token 仍有效，下次请求会正常进行
      // 如果 access_token 已失效，401 处理器会触发刷新或登出
      console.warn('[OIDC] UserSignedOut 事件触发，但不执行强制登出。下次 API 请求将验证 token 有效性。');

      // 记录事件供调试
      sessionStorage.setItem('user_signed_out_event', JSON.stringify({
        time: new Date().toISOString(),
        handled: 'deferred_to_401_handler'
      }));
    });
  }
  return _userManager;
};

// 初始化UserManager实例
export const mgr = getUserManager();

// TODO(human): Implement withTimeout utility function
// This function should wrap any Promise<T> with a timeout mechanism using Promise.race()
// Example signature: export const withTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => { ... }
// The timeout promise should reject with an error containing "SSO timeout" in the message
// This will be used to protect signinRedirect() calls from hanging indefinitely

// 公共方法
// ✅ 优化 #2: 移除冗余的手动OIDC配置获取
// UserManager在构造时已通过authority URL自动获取配置，无需手动fetch
// grep验证: sessionStorage.getItem('oidc.config') 在整个代码库中0次使用
export const signInRedirect = async (): Promise<void> => {


  // 保存完整的当前URL作为state，用于登录后返回
  const currentUrl = window.location.href;


  // 确保UserManager已初始化
  const manager = getUserManager();

  // 执行重定向
  return manager.signinRedirect({
    state: currentUrl
  });
};

export const signInCallback = (): Promise<User> => {

  return getUserManager().signinRedirectCallback();
};

export const signOut = (): Promise<void> => {

  localStorage.removeItem('access_token');
  localStorage.removeItem('id_token');

  return getUserManager().signoutRedirect().catch(error => {

    window.location.href = BASE_URL;
    return Promise.reject(error);
  });
};

export const silentRenew = (): Promise<User | null> => {

  return getUserManager().signinSilent().catch(() => null);
};

export const getUser = (): Promise<User | null> => {
  return getUserManager().getUser().then(user => {
    if (user) {

    } else {

    }
    return user;
  });
};

export const hasValidToken = async (): Promise<boolean> => {
  const user = await getUser();
  const isValid = !!user && !user.expired;

  return isValid;
};

// 获取访问令牌
export const getAccessToken = async (): Promise<string | null> => {
  const user = await getUser();
  if (user?.access_token) {
    return user.access_token;
  }
  return null;
};

// 调试函数：打印当前OIDC状态
export const debugOidcState = async (): Promise<void> => {
  try {
    const user = await getUser();
    void user;







  } catch (error) {
    void error;
  }
};

// 添加用户登出事件处理
export const addUserSignedOutEvent = (callback: () => void): void => {
  getUserManager().events.addUserSignedOut(callback);
};

// 添加静默刷新token失败事件处理
export const addSilentRenewErrorEvent = (callback: (error: Error) => void): void => {
  getUserManager().events.addSilentRenewError(callback);
};

// ================ 调试代码 ================
// ✅ 优化 #3c: 条件化配置表格输出 - 仅开发环境打印
if (import.meta.env.MODE === 'development') {

}
